import { ContactButton, RepresentationSection, Tooltip } from "@/components"
import { ProfessionalAssociationSection } from "@/components/MetadataSections/ProfessionalAssociationSection"
import { SubscriptionCheckoutModal } from "@/components/Subscriptions/SubscriptionCheckoutModal"
import { Button, Text } from "@/elements"
import { TextLink } from "@/elements/TextLink"
import { FragmentType, graphql, useFragment } from "@/gql"
import type { NovaSession } from "@/types"
import { LockIcon } from "@/icons/outline"
import { ProLockIcon } from "@/icons/outline/ProLockIcon"
import clsx from "clsx"
import Link from "next/link"
import { FC, useState } from "react"
import { PortfolioEditModal, useEditPortfolio } from "./edit/useEditPortfolio"
import { AboutRightContentIsProFragmentDoc } from "@/gql/graphql"
import { ContactInfoUpsellModal } from "@/components/Subscriptions/ContactInfoUpsellModal"
import { ampli } from "@/ampli"

const AboutRightContentFragment = graphql(`
  fragment AboutRightContent on IndividualCreative {
    __typename
    name
    id
    contactEmail
    website
    vimeoUrl
    linkedinUsername
    twitterUsername
    instagramUsername
    representations {
      edges {
        node {
          id
        }
      }
    }
    associationMemberships {
      edges {
        node {
          id
        }
      }
    }
    ...RepresentationSection
    ...ProfessionalAssociationSection
  }
`)

graphql(`
  fragment AboutRightContentIsPro on IndividualCreative {
    id
    proMembership {
      id
      activeMembershipType
    }
  }
`)

interface Props {
  creative: FragmentType<typeof AboutRightContentFragment> | undefined | null
  serverSession: NovaSession | null
  editProfileMethods?: ReturnType<typeof useEditPortfolio>
  viewerCreative: FragmentType<typeof AboutRightContentIsProFragmentDoc> | undefined | null
}

export const AboutRightContent: FC<Props> = props => {
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false)
  const [contactUpsellModalOpen, setContactUpsellModalOpen] = useState(false)
  const creative = useFragment(AboutRightContentFragment, props.creative)
  const viewerCreative = useFragment(AboutRightContentIsProFragmentDoc, props?.viewerCreative)
  const trackingData = { id: creative?.id, type: "creative" as const }
  const { serverSession, editProfileMethods } = props

  const hasContactInfo =
    !!creative?.contactEmail ||
    !!creative?.website ||
    !!creative?.twitterUsername ||
    !!creative?.instagramUsername ||
    !!creative?.vimeoUrl

  const onEditClick = () => {
    editProfileMethods?.setOpenModal(PortfolioEditModal.EditContactInfo)
  }

  const isAuthed = !!serverSession?.user
  const isPro = Boolean(viewerCreative?.proMembership?.activeMembershipType)
  const isUsersProfile = serverSession?.user.creative.id === creative?.id
  const upsellSubscription = !(isPro || isUsersProfile || serverSession?.account === "client")

  const onClickContact = () => {
    if (upsellSubscription) {
      ampli.upsellModalView({
        entry_point: "contact-info",
      })
      setContactUpsellModalOpen(true)
    }
  }

  const handleModalOpenChange = (open: boolean) => {
    if (!open) {
      ampli.upsellModalExit({
        entry_point: "contact-info",
      })
    }
    setContactUpsellModalOpen(open)
  }

  if (!isAuthed) {
    return (
      <div
        className={clsx(
          "flex flex-col items-center gap-y-6 rounded-lg bg-bg-2 px-7 py-8",
          "opacity-0 transition-opacity duration-300",
          !!creative?.name ? "opacity-100" : ""
        )}>
        <div className="flex flex-col items-center gap-y-4">
          <div className="flex size-12 items-center justify-center rounded-full bg-bg-1">
            <LockIcon className="size-5 stroke-fg-primary" />
          </div>
          <Text variant="h6" className={clsx("text-balance text-center")}>
            Sign up to view {creative?.name.split(" ")[0] || "user"}&apos;s contact information
          </Text>
        </div>
        <Link href="/sign-up">
          <Button>Sign up for Nova</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Need to always render subscription modal for success screen */}
      <SubscriptionCheckoutModal
        open={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        onOpenChange={setSubscriptionModalOpen}
        entryPoint="contact-info"
      />
      <div className="flex flex-col gap-3 rounded-lg bg-bg-2 p-5">
        <div
          className={clsx("mb-1 flex flex-row items-center gap-1", editProfileMethods?.isEditing && "justify-between")}>
          <Text>Contact</Text>

          {upsellSubscription ? (
            <>
              <Tooltip
                trigger={<ProLockIcon className="size-5" />}
                text={`Join NOVA Pro to access ${creative?.name.split(" ")[0]}’s contact information`}
                button={{
                  onClick: onClickContact,
                  children: (
                    <Text variant="CTASmall">
                      Sign up for{" "}
                      <Text variant="CTASmall" element="span" className="text-pro-gradient">
                        NOVA Pro
                      </Text>
                    </Text>
                  ),
                }}
                width={269}
              />
              <ContactInfoUpsellModal
                creativeFirstName={creative?.name.split(" ")[0] || ""}
                isOpen={contactUpsellModalOpen}
                onOpenChange={handleModalOpenChange}
                onClickGoPro={() => {
                  setContactUpsellModalOpen(false)
                  setSubscriptionModalOpen(true)
                  ampli.upsellModalClickThrough({
                    entry_point: "contact-info",
                  })
                }}
              />
            </>
          ) : null}
          {editProfileMethods?.isEditing && <TextLink text="Edit" underline onClick={onEditClick} />}
        </div>
        {!hasContactInfo && (
          <Text variant="body3">This creative hasn&apos;t provided any contact information yet.</Text>
        )}
        {creative?.contactEmail && (
          <ContactButton
            url={`mailto:${creative.contactEmail}?subject=[From NOVA]:`}
            type="email"
            serverSession={serverSession}
            label={upsellSubscription ? "Email" : creative.contactEmail}
            tracking={trackingData}
            upsellSubscription={upsellSubscription}
            openProModal={onClickContact}
          />
        )}
        {creative?.website && (
          <ContactButton
            url={creative.website}
            type="website"
            label="Website"
            tracking={trackingData}
            serverSession={serverSession}
            upsellSubscription={upsellSubscription}
            openProModal={onClickContact}
          />
        )}
        {creative?.twitterUsername && (
          <div className="flex flex-row items-center gap-2">
            <ContactButton
              url={`https://twitter.com/${creative?.twitterUsername}`}
              type="twitter"
              tracking={trackingData}
              label="Twitter"
              serverSession={serverSession}
              upsellSubscription={upsellSubscription}
              openProModal={onClickContact}
            />
          </div>
        )}
        {creative?.instagramUsername && (
          <div className="flex flex-row items-center gap-2">
            <ContactButton
              url={`https://www.instagram.com/${creative.instagramUsername}`}
              type="instagram"
              tracking={trackingData}
              label="Instagram"
              serverSession={serverSession}
              upsellSubscription={upsellSubscription}
              openProModal={onClickContact}
            />
          </div>
        )}
        {creative?.vimeoUrl && (
          <div className="flex flex-row items-center gap-2">
            <ContactButton
              url={creative.vimeoUrl}
              type="vimeo"
              tracking={trackingData}
              label="Vimeo"
              serverSession={serverSession}
              upsellSubscription={upsellSubscription}
              openProModal={onClickContact}
            />
          </div>
        )}
      </div>
      {!!creative?.representations?.edges?.length && creative?.representations?.edges?.length > 0 && (
        <RepresentationSection
          openProModal={onClickContact}
          creative={creative}
          upsellSubscription={upsellSubscription}
        />
      )}
      {!!creative?.associationMemberships?.edges?.length && creative?.associationMemberships?.edges?.length > 0 && (
        <ProfessionalAssociationSection creative={creative} />
      )}
    </div>
  )
}
